import { ImportTypes, clowncar } from 'types';
/* eslint-disable no-useless-escape */
import { ImportData } from 'routes/logged-in/imports/ImporterRoute';

import _ from 'lodash';
import protos from 'utils/protos';

const { PreImportFileData, ImporterConfig, PreImportReportInformation } = protos;

export const separatorNames = ['Tabs', 'Commas', 'Semi-Colon'];
export const nameOrderOptions = ['FIRST_LAST', 'LAST_FIRST'];

export function getEnv(environment: string): string {
  switch (environment) {
    case 'local': {
      return 'LOCAL';
    }
    case 'staging': {
      return 'STAGING';
    }
    case 'demo': {
      return 'DEMO';
    }
    case '': {
      return 'PROD';
    }
    default: {
      return '';
    }
  }
}

export function getImportConfigFromType(importerType: ImportTypes): string {
  switch (importerType) {
    case 'Flexible Importer': {
      return 'FLEXIBLE_IMPORTER';
    }
    case 'Waitlist': {
      return 'WAITLIST';
    }
    default: {
      return '';
    }
  }
}

export function getConfigString(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input: any,
  guestFileName?: string,
  resosFileName?: string
): string {
  return `Run through the self serve importer in clowncar \`\.\/run_importer.sh ${guestFileName} ${
    !_.isEmpty(resosFileName) ? resosFileName : 'empty.txt'
  }
   \"\{formatVersion:${getImportConfigFromType(input.importerType)},
    resoDateFormat:${input.resoDateFormat},resoTimeFormat:${input.resoTimeFormat},
    resoCreationDateFormat:${input.resoCreationDateFormat},guestDateFormat:${
      input.guestDateFormat
    },quoteDetectionEnabled: ${input.quoteDetectionEnabled},
  smsReminderEnabled: ${input.smsReminderEnabled},
  separator:\'${getSeparatorSymbol(input.separator)}\',
  resumeFromChunkId: ${input.resumeFromChunkId}\}\"\``;
}

export function getSeparatorSymbol(separatorName: string) {
  switch (separatorName) {
    case 'Commas': {
      return ',';
    }
    case 'Tabs': {
      return '/t';
    }
    case 'Semi-Colon': {
      return ';';
    }
    default: {
      return 'does not exist';
    }
  }
}

export function fileHasErrors(file: clowncar.IPreImportFileData, hasErrorsInitialized: boolean) {
  file.errorMessage?.forEach((errorMessage) => {
    hasErrorsInitialized =
      hasErrorsInitialized || _.toNumber(errorMessage.errorCode) !== _.toNumber(8024); // all errors not marked as a warning
  });

  return hasErrorsInitialized;
}

export function fileHasWarnings(file: clowncar.IPreImportFileData, hasErrorsInitialized: boolean) {
  file.errorMessage?.forEach((errorMessage) => {
    hasErrorsInitialized =
      hasErrorsInitialized || _.toNumber(errorMessage.errorCode) === _.toNumber(8024); // warning error code
  });

  return hasErrorsInitialized;
}

export function getFileData(
  filesData: clowncar.IPreImportFileData[],
  fileType: clowncar.PreImportFileData.FileType
) {
  let data = null;
  filesData.forEach((fileData) => {
    if (fileData.fileType && PreImportFileData.FileType[fileData.fileType] === fileType) {
      data = fileData;
    }
  });
  return data;
}

export function getPercentage(
  dividend: Number | undefined | null,
  divisor: Number | undefined | null
) {
  return dividend && divisor && Math.ceil((_.toNumber(dividend) / _.toNumber(divisor)) * 100);
}

export function parseMonthFromDate(date: string): string {
  return new Date(date).getMonth().toString();
}

export function parseYearFromDate(date: string): number {
  return new Date(date).getFullYear();
}

export function createBuckets(resos: clowncar.IImportReservation[]) {
  const resosByDay: Record<string, number> = {};
  resos.forEach((reso) => {
    if (reso.date) {
      resosByDay[reso.date] = resosByDay[reso.date] ? resosByDay[reso.date] + 1 : 1;
    }
  });
  if (Object.keys(resosByDay).length > 1000) {
    const resosByYear: Record<string, number> = {};
    let bucketKey = '';
    resos.forEach((reso) => {
      if (reso.date) {
        if (parseYearFromDate(bucketKey) !== parseYearFromDate(reso.date)) {
          bucketKey = reso.date;
        }
        if (bucketKey) {
          resosByYear[bucketKey] = resosByYear[bucketKey] ? resosByYear[bucketKey] + 1 : 1;
        }
      }
    });
    return resosByYear;
  }
  return resosByDay;
}

export function createImportObject(
  bizName: string | undefined,
  env: string,
  input: ImportData | undefined,
  preImportGuestFileData?: clowncar.IPreImportFileData,
  preImportResoFileData?: clowncar.IPreImportFileData,
  preImportWaitlistFileData?: clowncar.IPreImportFileData
): clowncar.IPreImportReportInformation {
  const fileData = [];
  preImportGuestFileData && fileData.push(preImportGuestFileData);
  preImportResoFileData && fileData.push(preImportResoFileData);
  preImportWaitlistFileData && fileData.push(preImportWaitlistFileData);

  const x = {
    businessName: bizName,
    environment: PreImportReportInformation.Environment[env],
    businessId: _.toNumber(input?.businessId),
    filesData: fileData,
    fileConfig: input && createImporterConfig(input),
  };

  return x;
}

export function createImporterConfig(input: ImportData): clowncar.IImporterConfig {
  return {
    separator: getSeparatorSymbol(input.separator),
    smsReminderEnabled: input.smsReminderEnabled,
    skipWalkins: input.skipWalkins,
    allowReimport: input.allowReimport,
    allowRebalancing: input.allowRebalancing,
    assignToExistingSlots: input.assignToExistingSlots,
    formatVersion: ImporterConfig.FormatVersion[getImportConfigFromType(input.importerType)],
    providedVipTag: input.providedVipTags,
    existingTicketTypeId: input.existingTicketTypeId
      ? parseInt(input.existingTicketTypeId)
      : undefined,
    guestFullNameOrder: ImporterConfig.NameOrder[input.guestFullNameOrder],
    resoFullNameOrder: ImporterConfig.NameOrder[input.resoFullNameOrder],
    multipleHeaderDetection: input.multipleHeaderDetection,
    markForDeletion: input.markForDeletion,
    disableIndexing: input.disableIndexing,
  } as clowncar.IImporterConfig;
}
``;
export function numberWithCommas(num: number | null | undefined) {
  return num && num.toLocaleString();
}
